import { useEffect } from 'react';
import type * as React from 'react';
import { Logo } from '@/components/logo';

/**
 * The frame for the two unauthenticated screens. Deliberately narrow and quiet:
 * these are the only pages an admin sees before they have a role, so there is
 * no navigation to draw and nothing to decide except the task in front of them.
 */
export function AuthShell({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}): React.JSX.Element {
  useEffect(() => {
    document.title = `${title} · Baby Sesame Admin`;
  }, [title]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-7 flex items-center gap-3">
          {/* The wordmark carries the name, so the text beside it says only
              which surface this is — repeating "Baby Sesame" would be reading
              the logo aloud. */}
          <Logo className="h-7" />
          <span aria-hidden="true" className="h-5 w-px bg-line-strong" />
          <span className="text-sm font-semibold tracking-tight text-ink-soft">Admin</span>
        </div>

        <div className="rounded-card border border-line bg-surface p-6 shadow-sm">
          <h1 className="text-lg font-semibold tracking-tight text-ink">{title}</h1>
          <p className="mt-1.5 text-sm text-ink-soft">{description}</p>
          <div className="mt-6">{children}</div>
        </div>

        {footer ? <div className="mt-5 text-xs text-ink-faint">{footer}</div> : null}
      </div>
    </div>
  );
}
